import { Component, EventEmitter, Input, OnInit, Output, Injector } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { BaseModalComponent } from '../../base-modal.component';
import { ApplicationService } from '../../../../services/application/application.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ModalService } from '../../../../services/modal/modal.service';
import { AmorphicService } from '../../../../services/amorphic/amorphic.service';
import type { Policy } from '../../../../../../common/js/customer/Policy';
import { PutObjectOutput } from 'aws-sdk/clients/s3';
import { PdfService } from '../../../../services/pdf/pdf.service';
import { AccountCenterService } from '../../../../services/account-center/account-center.service';
import { RequiredDocumentsService, UploadSettings } from '../../../../../../common/js/services/RequiredDocumentService';

interface ImageBlob extends Blob {
  name: string;
}

interface UploadDataObject {
  ETag: string;
  Body: string;
  LastModified: Date;
}

interface UploadData {
  Object: UploadDataObject;
  Name: string;
}

class UploadDocObject {
  eTag: string;
  body;
  file: File;
  lastModified: Date;
  fileName: string;

  constructor(file: File, body: any) {
    this.file = file;
    this.body = body;
    this.fileName = file.name;
  }
}

@Component({
  moduleId: module.id,
  selector: 'document_upload',
  templateUrl: './document_upload.component.html',
})
export class DocumentUploadComponent extends BaseModalComponent implements OnInit {
  error: string;
  fileName: string;
  uploadDate: string;
  resultFile;
  policy: Policy;
  docType: string;
  uploadType: string;
  controller;
  @Output() removePendingUpload: EventEmitter<any> = new EventEmitter();
  preview: string | ArrayBuffer;
  file: File;
  uploadDocObjectList: UploadDocObject[] = [];
  accountCenterService: AccountCenterService;
  static readonly maxNumberOfImages: number = 4;
  static readonly maxFileSize: number = 5 * 1024 * 1024;
  static readonly maxNumberMsg: string = 'Only 4 documents are allowed to upload.';
  static readonly fileExistMsg = 'already exists in the preview area.';

  protected uploadSettings: UploadSettings;

  uploadFormGroup: FormGroup;
  constructor(
    private amorphicService: AmorphicService,
    private appService: ApplicationService,
    private router: Router,
    private formBuilder: FormBuilder,
    private sanitizer: DomSanitizer,
    modalService: ModalService,
    accountCenterService: AccountCenterService,
    protected injector: Injector,
    private RequiredDocService: RequiredDocumentsService
  ) {
    super(modalService);
    this.controller = amorphicService.controller;
    this.policy = this.controller.customer.application.primaryPolicy;
    this.uploadSettings = this.RequiredDocService.GetUploadSettings();
    this.docType = this.uploadSettings.RequiredDocumentType;

    this.accountCenterService = accountCenterService;
    this.uploadFormGroup = this.formBuilder.group({
      fileChoose: undefined,
    });
  }

  ngOnInit(): void {}

  cancel() {
    this.modalService.deactivate();
  }

  async confirm() {
    if (this.uploadDocObjectList.length > 0) {
      for (const obj of this.uploadDocObjectList) {
        this.triggerUpload(obj.file);
      }

      await this.controller.accountCenterController.checkRequirement(
        this.policy,
        'UD1R1 Customer document uploaded',
        'Review Document',
        'Review Document'
      );

      this.accountCenterService.workflowStateSubject.next('Review Document:Review Document');
      this.modalService.deactivate();
    }
  }

  deleteFile(fileName: string) {
    for (let i = 0; i < this.uploadDocObjectList.length; i++) {
      if (this.uploadDocObjectList[i].fileName === fileName) {
        this.uploadDocObjectList.splice(i, 1);
      }
    }

    if (
      this.error === DocumentUploadComponent.maxNumberMsg ||
      this.error.indexOf(DocumentUploadComponent.fileExistMsg) > 0
    ) {
      this.error = '';
    }
  }

  onClick(event: Event): void {
    const element = event.target as HTMLInputElement;
    element.value = '';
  }

  isFileEligible(file: File): boolean {
    if (file.size > DocumentUploadComponent.maxFileSize) {
      this.error = 'Sorry, we accept files that are below 5MB only';
      return false;
    }

    return true;
  }

  isFileDuplicate(file: File): boolean {
    for (let i = 0; i < this.uploadDocObjectList.length; i++) {
      if (this.uploadDocObjectList[i].fileName === file.name) {
        this.error = `${file.name} already exists in the preview area.`;
        return true;
      }
    }

    return false;
  }

  isNumberOfFileExceedLimit(target: HTMLInputElement): boolean {
    if (this.uploadDocObjectList.length + target.files.length > this.uploadSettings.MaxUploads) {
      this.error = DocumentUploadComponent.maxNumberMsg;
      return false;
    }
    return true;
  }

  async onChange(event: Event) {
    const eventObj: Event = event;
    const target: HTMLInputElement = <HTMLInputElement>eventObj.target;

    if (!this.isNumberOfFileExceedLimit(target)) {
      return;
    }

    for (let i = 0; i < target.files.length; i++) {
      const file = target.files[i];

      if (file) {
        if (!this.isFileEligible(file) || this.isFileDuplicate(file)) {
          return;
        }

        this.error = '';
        const date = new Date();
        this.uploadDate = date.toDateString().substr(4);
        this.uploadType = file.type;

        const reader = new FileReader();
        reader.readAsDataURL(file); // read file as data url
        if (file.type.match('pdf')) {
          reader.onload = async (event) => {
            // called once readAsDataURL is completed
            try {
              const canvas = <HTMLCanvasElement>document.getElementById('pdf-canvas');
              const imageData = await PdfService.renderPdfImage(<string>reader.result, canvas, 1);
              const uploadDocObject: UploadDocObject = new UploadDocObject(file, imageData);
              this.uploadDocObjectList.splice(0, 0, uploadDocObject);
            } catch (e) {
              this.error = `Failed to load PDF file: ${file.name}.`;
              return;
            }
          };
        } else if (file.type.match('image') && !file.type.match('ico') && !file.type.match('tif')) {
          reader.onload = (event) => {
            // called once readAsDataURL is completed
            const uploadDocObject: UploadDocObject = new UploadDocObject(file, reader.result);
            this.uploadDocObjectList.splice(0, 0, uploadDocObject);
          };
        } else {
          this.error = 'Sorry, we only accept JPG, PNG, GIF, and PDF file types. Please try again.';
        }
      } else {
        this.triggerError();
      }
    }
  }

  triggerUpload(file: File) {
    this.resultFile = file;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = <string>reader.result;
      this.setFileName(file.name);
      this.controller.remoteDocServiceController
        .uploadFile(base64, file.name, this.policy, this.docType)
        .then((data) => this.confirmFileUpload(data))
        .catch((err) => {
          this.triggerError();
          throw Error(`Failed to upload ${file.name}`);
        });
    };
    reader.onerror = (error) => {
      this.triggerError();
    };
  }

  triggerError(downloadError?: boolean) {
    this.error = downloadError
      ? `Sorry, we’re having trouble downloading that file. Please try again.`
      : `Sorry, we’re having trouble uploading that file. Please try again.`;
    this.fileName = undefined;
  }

  setFileName(fileName: string) {
    this.fileName = fileName.length <= 20 ? fileName : fileName.substring(0, 17) + '~.' + fileName.split('.').pop();
  }

  confirmFileUpload(data: void | PutObjectOutput) {
    if ((<UploadDataObject>data).ETag) {
      this.removePendingUpload.emit();
    } else {
      this.triggerError();
    }
  }

  isDisabled(): boolean {
    return this.uploadDocObjectList.length === 0;
  }
}
